/** O painel dos conflitos em três passos: Detetar, Rever, Entregar BCF.
 *
 *  Pedido do Rui (15/10/2026): "a parte da detecção de conflitos e
 *  gravar em bcf e ver a lista de conflitos está estranha, não me parece
 *  intuitivo". Era tudo uma coluna: a lista só aparecia depois de uns dez
 *  blocos de configuração, partilha e exportação. Aqui ficam as contas
 *  que os separadores mostram; o painel é ClashWorkbench.tsx. */
import { clashKey, type ClashPriority, type ClashTriageEntry } from './clashTriage'
import type { ClashEnvioModulo } from './clashReconciliacao'
import type { ClashResult } from '@viewer/clash/lib'
import { formatarInteiro } from '@modules/bcf/lib/bcfAgrupar'

export type SeparadorDosConflitos = 'detetar' | 'rever' | 'entregar'

export type ContagemDePrioridades = Record<ClashPriority, number> & { sem: number }

export function contarPrioridades(
  results: readonly ClashResult[],
  triage: Readonly<Record<string, ClashTriageEntry | undefined>>,
): ContagemDePrioridades {
  const c: ContagemDePrioridades = { critica: 0, alta: 0, media: 0, baixa: 0, sem: 0 }
  for (const r of results) {
    const p = triage[clashKey(r)]?.priority
    if (p && p in c) c[p] += 1
    else c.sem += 1
  }
  return c
}

const NOMES: [ClashPriority, string, string][] = [
  ['critica', 'crítico', 'críticos'],
  ['alta', 'alto', 'altos'],
  ['media', 'médio', 'médios'],
  ['baixa', 'baixo', 'baixos'],
]

/** "1100 conflitos: 40 críticos, 30 altos, 468 médios, 648 baixos". */
export function textoDoResumoDaCorrida(total: number, prio: ContagemDePrioridades): string {
  const cabeca = `${formatarInteiro(total)} conflito${total === 1 ? '' : 's'}`
  const partes = NOMES.filter(([p]) => prio[p] > 0).map(([p, um, varios]) => `${prio[p]} ${prio[p] === 1 ? um : varios}`)
  if (prio.sem > 0 && partes.length > 0) partes.push(`${prio.sem} sem prioridade`)
  return partes.length > 0 ? `${cabeca}: ${partes.join(', ')}` : cabeca
}

/** Os estados BCF que contam como fechado. */
const FECHADOS = new Set(['closed', 'resolved', 'done', 'fechado', 'resolvido'])

/** Para cada grupo (ou conflito) da lista: o que está no BCF do módulo.
 *  "sem tópico", "tópico aberto", "resolvido", ou "2 de 5 com tópico". */
export function estadoNoBcf(
  envio: ClashEnvioModulo | null,
  topicos: readonly { guid: string; status?: string | null }[],
): (chaves: readonly string[]) => string | null {
  if (!envio) return () => null
  const guidDaChave = new Map<string, string>()
  for (const t of Object.values(envio.topicos)) for (const k of t.chaves) guidDaChave.set(k, t.guid)
  const estadoDoGuid = new Map(topicos.map((t) => [t.guid, (t.status ?? '').trim().toLowerCase()]))
  return (chaves) => {
    if (chaves.length === 0) return null
    const guids = chaves.map((k) => guidDaChave.get(k)).filter((g): g is string => !!g && estadoDoGuid.has(g))
    if (guids.length === 0) return 'sem tópico'
    if (guids.length < chaves.length) return `${guids.length} de ${chaves.length} com tópico`
    return guids.every((g) => FECHADOS.has(estadoDoGuid.get(g) ?? '')) ? 'resolvido' : 'tópico aberto'
  }
}
